import ajax from '@/lib/ajax';
import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import { AppModule } from '@/store/modules/app';
import OpportunityDto from '@/models/Opportunities/OpportunityDto';
import CreateOpportunityRequest from '@/models/Opportunities/CreateOpportunityRequest';
import ConvertOpportunityRequest from '@/models/Opportunities/ConvertOpportunityRequest';
import ConvertOpportunityResponse from '@/models/Opportunities/ConvertOpportunityResponse';
import DocumentDto from '@/models/Documents/DocumentDto';
import UploadDocumentRequest from '@/models/Documents/UploadDocumentRequest';
import CreateDocumentRequest from '@/models/Documents/CreateDocumentRequest';
import UpdateDocumentRequest from '@/models/Documents/UpdateDocumentRequest';
import FileWithExpiry from '@/models/Documents/FileWithExpiry';
import FileUtils from '@/lib/fileUtils';

export interface IOpportunityDetailsState {
  loading: boolean;
  saving: boolean;
  opportunity: OpportunityDto | null;
  documents: DocumentDto[];
  loadingDocuments: boolean;
  loadingDialog: boolean;
  showConvertWizard: boolean;
}

@Module({ dynamic: true, store, name: 'OpportunityDetails', namespaced: true })
class OpportunityDetails extends VuexModule implements IOpportunityDetailsState {
  public loading = false;
  public saving = false;
  public opportunity: OpportunityDto | null = null;
  public documents: DocumentDto[] = [];
  public loadingDocuments = true;
  public loadingDialog = false;
  // Whether the inline Convert-to-Won wizard replaces the detail tabs.
  public showConvertWizard = false;

  @Mutation
  setLoading(v: boolean) { this.loading = v; }

  @Mutation
  setShowConvertWizard(v: boolean) { this.showConvertWizard = v; }

  @Mutation
  setSaving(v: boolean) { this.saving = v; }

  @Mutation
  setOpportunity(opp: OpportunityDto | null) { this.opportunity = opp; }

  @Mutation
  setDocuments(documents: DocumentDto[]) {
    this.documents = documents || [];
  }

  @Mutation
  setDocumentFromUpload(document: DocumentDto) {
    this.documents.push(document);
  }

  @Mutation
  setDocumentFromEdit(document: DocumentDto) {
    const index = this.documents.findIndex(d => d.id === document.id);
    if (index >= 0) {
      this.documents.splice(index, 1, document);
    }
  }

  @Mutation
  setLoadingDocuments(v: boolean) { this.loadingDocuments = v; }

  @Mutation
  setLoadingDialog(v: boolean) { this.loadingDialog = v; }

  @Action
  async load(id: string) {
    this.setLoading(true);
    this.setShowConvertWizard(false);
    try {
      const response = await ajax.get(`/api/v1/agency/opportunities/${id}`);
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
      this.setOpportunity(null);
    } finally {
      this.setLoading(false);
    }
  }

  @Action
  async create(request: CreateOpportunityRequest): Promise<OpportunityDto | null> {
    this.setSaving(true);
    try {
      const response = await ajax.post('/api/v1/agency/opportunities', request);
      const opp = response.data as OpportunityDto;
      this.setOpportunity(opp);
      return opp;
    } catch (error) {
      AppModule.handleApiError(error);
      return null;
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async setStage(payload: { id: string; stage: number; lostReasonId?: string | null; lostReasonNotes?: string; updatedAt: string }) {
    this.setSaving(true);
    try {
      const response = await ajax.put(`/api/v1/agency/opportunities/${payload.id}/stage`, {
        stage: payload.stage,
        lostReasonId: payload.lostReasonId ?? null,
        lostReasonNotes: payload.lostReasonNotes ?? '',
        updatedAt: payload.updatedAt,
      });
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async setStatus(payload: { id: string; status: number; updatedAt: string }) {
    this.setSaving(true);
    try {
      const response = await ajax.put(`/api/v1/agency/opportunities/${payload.id}/status`, {
        status: payload.status,
        updatedAt: payload.updatedAt,
      });
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async updateGeneralDetails(payload: any) {
    this.setSaving(true);
    try {
      // Strip `id` — it's only used to build the URL, not part of the request body.
      const { id, ...body } = payload;
      const response = await ajax.put(`/api/v1/agency/opportunities/${id}/general-details`, body);
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async updateLinks(payload: { id: string; companyId: string | null; locationId: string | null; contactId: string | null; updatedAt: string }) {
    this.setSaving(true);
    try {
      const response = await ajax.put(`/api/v1/agency/opportunities/${payload.id}/links`, {
        companyId: payload.companyId,
        locationId: payload.locationId,
        contactId: payload.contactId,
        updatedAt: payload.updatedAt,
      });
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  // Prospect-snapshot updates — split per-entity per-section. Each action
  // posts only the fields its corresponding edit dialog owns, so two
  // dialogs touching different sections can no longer clobber each other.
  @Action
  async updateProspectCompanyDetails(payload: any) {
    this.setSaving(true);
    try {
      const { id, ...body } = payload;
      const response = await ajax.put(`/api/v1/agency/opportunities/${id}/prospect-company-details`, body);
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async updateProspectCompanyAddress(payload: any) {
    this.setSaving(true);
    try {
      const { id, ...body } = payload;
      const response = await ajax.put(`/api/v1/agency/opportunities/${id}/prospect-company-address`, body);
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async updateProspectLocationDetails(payload: any) {
    this.setSaving(true);
    try {
      const { id, ...body } = payload;
      const response = await ajax.put(`/api/v1/agency/opportunities/${id}/prospect-location-details`, body);
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async updateProspectLocationAddress(payload: any) {
    this.setSaving(true);
    try {
      const { id, ...body } = payload;
      const response = await ajax.put(`/api/v1/agency/opportunities/${id}/prospect-location-address`, body);
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async updateProspectContact(payload: any) {
    this.setSaving(true);
    try {
      const { id, ...body } = payload;
      const response = await ajax.put(`/api/v1/agency/opportunities/${id}/prospect-contact`, body);
      this.setOpportunity(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async convert(payload: { id: string; request: ConvertOpportunityRequest }): Promise<ConvertOpportunityResponse | null> {
    this.setSaving(true);
    try {
      const response = await ajax.post(
        `/api/v1/agency/opportunities/${payload.id}/convert`,
        payload.request,
      );
      // Refetch so the opportunity carries the new converted_* fields and the
      // backfilled company/location/contact links (the convert endpoint only
      // returns the bare IDs).
      await this.load(payload.id);
      return response.data as ConvertOpportunityResponse;
    } catch (error) {
      AppModule.handleApiError(error);
      return null;
    } finally {
      this.setSaving(false);
    }
  }

  @Action
  async updateDescription(payload: { id: string; richText: string; updatedAt: string }) {
    this.setSaving(true);
    try {
      const response = await ajax.put(`/api/v1/agency/opportunities/${payload.id}/description`, {
        richText: payload.richText,
        updatedAt: payload.updatedAt,
      });
      // The description endpoint returns just { updatedAt, richText } — patch the
      // current opportunity in place rather than reloading.
      if (this.opportunity) {
        this.opportunity.description = response.data.richText;
        this.opportunity.updatedAt = response.data.updatedAt;
      }
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setSaving(false);
    }
  }

  // ============================================================================
  // Documents — mirrors the pattern in companyDetails.ts / workerDetails.ts
  // ============================================================================

  @Action
  async getDocuments() {
    if (!this.opportunity) return;
    this.setLoadingDocuments(true);
    try {
      const response = await ajax.get(`/api/v1/agency/opportunities/${this.opportunity.id}/documents`);
      this.setDocuments(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setLoadingDocuments(false);
    }
  }

  @Action
  async uploadDocument(fileWithExpiry: FileWithExpiry) {
    if (!this.opportunity) return;

    const fileExtension = '.' + fileWithExpiry.file.name.split('.').pop();
    const contentType = FileUtils.cleanContentType(fileExtension, fileWithExpiry.file.type);
    const uploadRequest = new UploadDocumentRequest();
    uploadRequest.fileName = fileWithExpiry.file.name;
    uploadRequest.contentType = contentType;
    let destinationFileName = '';

    this.setLoadingDialog(true);
    try {
      const uploadResponse = await ajax.post(`/api/v1/agency/opportunities/${this.opportunity.id}/documents/upload`, uploadRequest);
      destinationFileName = uploadResponse.data.destinationFileName;

      await ajax.put(uploadResponse.data.signedUrl, fileWithExpiry.file, {
        headers: { 'content-type': contentType },
      });

      const createRequest = new CreateDocumentRequest();
      createRequest.originFileName = fileWithExpiry.file.name;
      createRequest.contentType = contentType;
      createRequest.destinationFileName = destinationFileName;
      createRequest.isExpiryTracked = fileWithExpiry.isExpiryTracked;
      createRequest.expiresAt = fileWithExpiry.expiresAt;
      const createResponse = await ajax.post(`/api/v1/agency/opportunities/${this.opportunity.id}/documents`, createRequest);
      this.setDocumentFromUpload(createResponse.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setLoadingDialog(false);
    }
  }

  @Action
  async downloadDocument(id: string) {
    if (!this.opportunity) return;
    this.setLoadingDialog(true);
    try {
      const response = await ajax.get(`/api/v1/agency/opportunities/${this.opportunity.id}/documents/${id}`);
      const element = document.createElement('a');
      element.setAttribute('href', response.data.signedUrl);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setLoadingDialog(false);
    }
  }

  @Action
  async editDocument(documentDto: DocumentDto) {
    if (!this.opportunity) return;
    const request = new UpdateDocumentRequest();
    request.updatedAt = documentDto.updatedAt;
    request.name = documentDto.name;
    request.isExpiryTracked = documentDto.isExpiryTracked;
    if (documentDto.expiresAt !== null) {
      request.expiresAt = new Date(documentDto.expiresAt).toISOString().substr(0, 10);
    }
    this.setLoadingDialog(true);
    try {
      const response = await ajax.put(`/api/v1/agency/opportunities/${this.opportunity.id}/documents/${documentDto.id}`, request);
      this.setDocumentFromEdit(response.data);
    } catch (error) {
      AppModule.handleApiError(error);
    } finally {
      this.setLoadingDialog(false);
    }
  }

  @Action
  cleanState() {
    this.context.commit('resetState');
  }

  @Mutation
  resetState() {
    this.loading = false;
    this.saving = false;
    this.opportunity = null;
    this.documents = [];
    this.loadingDocuments = true;
    this.loadingDialog = false;
  }
}

export const OpportunityDetailsModule = getModule(OpportunityDetails);
